import usePrevious from 'react-utils/hooks/usePrevious';
import { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { getModuleUndoCount, getModuleUndoRedoCount } from 'ContentEditorUI/redux/selectors/moduleSelectors';
import { getIsDataAuthoringModeActive } from 'ContentEditorUI/redux/selectors/instanceEditingSelectors';
import { selectInstanceEditingUndoCount, selectInstanceEditingUndoRedoCount } from 'structured-content-lib/redux/selectors/instanceEditSelectors';
import { addInlineEditor, removeInlineEditor } from './inlineEditingUtils';
import { getLocaleSeparators } from './localeUtils';
export const numberFormatter = (value, locale = 'en-US') => {
  const numValue = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(numValue)) {
    return String(value);
  }
  return new Intl.NumberFormat(locale).format(numValue);
};
export const currencyFormatter = (value, currencyCode = 'USD', locale = 'en-US') => {
  const numValue = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(numValue)) {
    return String(value);
  }
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currencyCode
  }).format(numValue);
};
export const percentageFormatter = (value, locale = 'en-US') => {
  const {
    decimalSeparator
  } = getLocaleSeparators(locale);
  const {
    before: charBeforeCursor,
    after: charAfterCursor
  } = getCharactersBeforeAndAfterCursor();
  let numValue = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(numValue)) {
    return String(value);
  }

  // Clamp percentage values between 0 and 100
  numValue = Math.max(0, Math.min(100, numValue));

  // Use style: 'percent' but divide by 100 first since Intl.NumberFormat multiplies by 100
  // This way "1" becomes "1%" instead of "100%"
  const formattedValue = new Intl.NumberFormat(locale, {
    style: 'percent',
    maximumFractionDigits: 2
  }).format(numValue / 100);

  // If there is a trailing decimal seperator in the text,
  // the above formattedValue stripped it out. So this will add it back in so the user can continue typing
  if (charBeforeCursor === decimalSeparator && !/\d/.test(charAfterCursor)) {
    const lastNumberIndex = formattedValue.search(/\d(?=\D*$)/);
    if (lastNumberIndex !== -1) {
      return formattedValue.slice(0, lastNumberIndex + 1) + decimalSeparator + formattedValue.slice(lastNumberIndex + 1);
    }
    return formattedValue + decimalSeparator;
  }
  return formattedValue;
};
export const unformatNumber = (value, locale = 'en-US') => {
  const {
    groupSeparator,
    decimalSeparator
  } = getLocaleSeparators(locale);

  // Remove group separators and everything that is not a digit or decimal separator
  const groupSeparatorEscaped = groupSeparator.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const decimalSeparatorEscaped = decimalSeparator.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const normalized = value.replace(new RegExp(groupSeparatorEscaped, 'g'), '') // remove group separators
  .replace(new RegExp(`[^\\d${decimalSeparatorEscaped}-]`, 'g'), '') // remove currency symbols, percent signs, keep digits, decimal, and minus
  .replace(new RegExp(decimalSeparatorEscaped, 'g'), '.'); // normalize decimal separator to dot

  return normalized;
};
const DATA_TOKEN_FORMAT_HANDLERS = {
  currency: currencyFormatter,
  percent: percentageFormatter,
  number: numberFormatter
};

/**
 * Parses the hs-data-token-format attribute from a DOM element
 * @param node The DOM element to check for formatting attributes
 * @returns An object containing formatType and formatArgs, or null if no format attribute
 *
 * @example
 * // For a DOM element with hs-data-token-format="currency,USD,nl"
 * const parsed = parseFormatterAttributes(node);
 * // Returns: { formatType: 'currency', formatArgs: ['USD', 'nl'] }
 */
export function parseFormatterAttributes(node) {
  const formatAttribute = node.getAttribute('hs-data-token-format');
  if (!formatAttribute) {
    return null;
  }
  const formatParts = formatAttribute.split(',');
  const formatType = formatParts[0];
  const formatArgs = formatParts.slice(1);
  return {
    formatType,
    formatArgs
  };
}

/**
 * Parses the hs-data-token-loading-dependencies attribute from a DOM element
 * @param node The DOM element to check for loading dependencies attributes
 * @returns An array of dependency strings, or null if no dependencies attribute
 *
 * @example
 * // For a DOM element with hs-data-token-loading-dependencies="quote.properties.total,quote.properties.discount"
 * const parsed = parseLoadingDependencies(node);
 * // Returns: ['quote.properties.total', 'quote.properties.discount']
 */
export function parseLoadingDependencies(node) {
  const dependenciesAttribute = node.getAttribute('hs-data-token-loading-dependencies');
  if (!dependenciesAttribute) {
    return null;
  }
  const dependencies = dependenciesAttribute.split(',').map(dep => dep.trim()).filter(dep => dep.length > 0);
  return dependencies.length > 0 ? dependencies : null;
}

/**
 * Gets the locale from a data token node, or falls back to en-US
 * @param node The DOM element to check for locale in formatting attributes
 * @returns The locale string (e.g., 'en-US', 'nl', 'de-DE')
 *
 * @example
 * // For a DOM element with hs-data-token-format="currency,USD,nl"
 * const locale = getDataTokenLocale(node);
 * // Returns: 'nl'
 *
 * // For a DOM element with no format attribute
 * const locale = getDataTokenLocale(node);
 * // Returns: 'en-US'
 */
export function getDataTokenLocale(node) {
  const parsed = parseFormatterAttributes(node);
  if (parsed) {
    if (parsed.formatType === 'currency' && parsed.formatArgs.length >= 2) {
      // For currency format: "currency,USD,nl" -> locale is the 3rd parameter
      return parsed.formatArgs[1];
    }
    if ((parsed.formatType === 'percent' || parsed.formatType === 'number') && parsed.formatArgs.length >= 1) {
      // For percent format: "percent,nl" -> locale is the 2nd parameter
      return parsed.formatArgs[0];
    }
  }

  // Could extend this for other format types that include locale
  // For now, fall back to en-US
  return 'en-US';
}
export function useInlineFieldEditingHoverStyles(node) {
  useEffect(() => {
    const onHover = () => {
      node.classList.add('inpage-inline-field-overlay');
    };
    const onLeave = () => {
      node.classList.remove('inpage-inline-field-overlay');
    };
    node.addEventListener('mouseover', onHover);
    node.addEventListener('mouseleave', onLeave);
    return () => {
      node.classList.remove('inpage-inline-field-overlay');
      node.removeEventListener('mouseover', onHover);
      node.removeEventListener('mouseleave', onLeave);
    };
  }, [node]);
}
export function useNoOutlineWhenFocused(node) {
  useEffect(() => {
    node.classList.add('inpage-inline-field-outline-none');
    return () => {
      node.classList.remove('inpage-inline-field-outline-none');
    };
  }, [node]);
}
export function useWasUndoRedoEvent() {
  const isDataAuthoringModeActive = useSelector(getIsDataAuthoringModeActive);
  const moduleUndoCount = useSelector(getModuleUndoCount);
  const moduleUndoRedoCount = useSelector(getModuleUndoRedoCount);
  const prevModuleUndoCount = usePrevious(moduleUndoCount);
  const prevModuleUndoRedoCount = usePrevious(moduleUndoRedoCount);
  const instanceEditingUndoCount = useSelector(selectInstanceEditingUndoCount);
  const instanceEditingUndoRedoCount = useSelector(selectInstanceEditingUndoRedoCount);
  const prevInstanceEditingUndoCount = usePrevious(instanceEditingUndoCount);
  const prevInstanceEditingUndoRedoCount = usePrevious(instanceEditingUndoRedoCount);
  if (isDataAuthoringModeActive) {
    return prevInstanceEditingUndoCount !== undefined && prevInstanceEditingUndoRedoCount !== undefined && (instanceEditingUndoCount !== prevInstanceEditingUndoCount || instanceEditingUndoRedoCount !== prevInstanceEditingUndoRedoCount);
  }
  return prevModuleUndoCount !== undefined && prevModuleUndoRedoCount !== undefined && (moduleUndoCount !== prevModuleUndoCount || moduleUndoRedoCount !== prevModuleUndoRedoCount);
}
export function useWasUndoRedoEventWithData(data) {
  const prevData = usePrevious(data);
  const wasUndoRedo = useWasUndoRedoEvent();
  return wasUndoRedo && prevData !== undefined && prevData !== data;
}
export function useRegisterInlineFieldEditor(moduleName) {
  useEffect(() => {
    addInlineEditor(moduleName);
    return () => {
      removeInlineEditor(moduleName);
    };
  }, [moduleName]);
}

/**
 *
 * @param moduleName unique name for module
 * @param fieldKeyString period separated key string for accessing the current field (i.e group1.headings.text)
 * @param fieldName name for the current field
 * @returns a group key used for creating undo/redo slices. In data authoring mode, we want to use
 * the field name since a) that will map straight back to the instance property name and b) we want to group updates
 * to the same property, even if those updates occurred in different places. Otherwise, it will create a key
 * that's unique based on the name of the module and the path to the field.
 */
export function useGroupKey(moduleName, fieldKeyString, fieldName) {
  const isDataAuthoringModeActive = useSelector(getIsDataAuthoringModeActive);
  return useMemo(() => isDataAuthoringModeActive ? fieldName : `${moduleName}-${fieldKeyString}`, [fieldKeyString, fieldName, isDataAuthoringModeActive, moduleName]);
}
export function useJoinKeyPathToString(keyPath) {
  return useMemo(() => {
    return keyPath.join('.');
  }, [keyPath]);
}

/**
 * Utility function that applies data token formatting based on the `hs-data-token-format` attribute
 * @param node The DOM element to check for formatting attributes
 * @param value The value to format
 * @returns The formatted value or original value if no formatting is applied
 *
 * @example
 * // For a DOM element with hs-data-token-format="currency,USD"
 * const formattedValue = formatDataTokenValue(node, 1234.56);
 * // Returns: "$1,234.56"
 *
 * // For a DOM element with hs-data-token-format="currency,EUR"
 * const formattedValue = formatDataTokenValue(node, 1234.56);
 * // Returns: "€1,234.56"
 */
export function formatDataTokenValue(node, value) {
  const parsed = parseFormatterAttributes(node);
  if (!parsed) {
    return String(value);
  }
  const {
    formatType,
    formatArgs
  } = parsed;
  if (!Object.prototype.hasOwnProperty.call(DATA_TOKEN_FORMAT_HANDLERS, formatType)) {
    console.warn(`Unknown data token format type: ${formatType}`);
    return String(value);
  }
  const formatter = DATA_TOKEN_FORMAT_HANDLERS[formatType];
  try {
    return formatter(value, ...formatArgs);
  } catch (error) {
    console.error(`Error applying format ${formatType} to value ${value}:`, error);
    return String(value);
  }
}

/**
 * Hook that applies data token formatting based on the `hs-data-token-format` attribute
 * @param node The DOM element to check for formatting attributes
 * @param value The value to format
 * @returns The formatted value or original value if no formatting is applied
 *
 * @example
 * // For a DOM element with hs-data-token-format="currency,USD"
 * const formattedValue = useDataTokenFormat(node, 1234.56);
 * // Returns: "$1,234.56"
 *
 * // For a DOM element with hs-data-token-format="currency,EUR,en-GB"
 * const formattedValue = useDataTokenFormat(node, 1234.56);
 * // Returns: "€1,234.56"
 */
export function useDataTokenFormat(node, value) {
  return useMemo(() => formatDataTokenValue(node, value), [node, value]);
}

/**
 * Gets the characters before and after the current cursor position using window.getSelection()
 * @returns An object with before and after characters, or empty strings if at boundaries or no selection
 */
export function getCharactersBeforeAndAfterCursor() {
  const selection = window.getSelection();
  if (!selection || selection.rangeCount === 0) {
    return {
      before: '',
      after: ''
    };
  }
  const range = selection.getRangeAt(0);
  const startOffset = range.startOffset;
  const startContainer = range.startContainer;
  if (startContainer.nodeType === Node.TEXT_NODE) {
    const textContent = startContainer.textContent || '';
    const before = startOffset > 0 ? textContent.charAt(startOffset - 1) : '';
    const after = startOffset < textContent.length ? textContent.charAt(startOffset) : '';
    return {
      before,
      after
    };
  }
  if (startContainer.nodeType === Node.ELEMENT_NODE) {
    const element = startContainer;
    if (element.tagName === 'INPUT' || element.tagName === 'TEXTAREA') {
      const inputElement = element;
      const value = inputElement.value;
      const selectionStart = inputElement.selectionStart || 0;
      const before = selectionStart > 0 ? value.charAt(selectionStart - 1) : '';
      const after = selectionStart < value.length ? value.charAt(selectionStart) : '';
      return {
        before,
        after
      };
    }
  }
  return {
    before: '',
    after: ''
  };
}